import type {
  Service, Pod, ClusterSummary, ClusterTopology, TopologyNode, TopologyEdge,
  HealthScore, K8sObjectSummary, DeploymentTimelineEntry, ContainerDetail,
} from "@coremonit/types";
import { makeRng, int } from "./rng";

const CLUSTER_META: Record<string, { provider: ClusterSummary["cloudProvider"]; region: string; k8s: string }> = {
  "prod-eks-01": { provider: "aws", region: "us-east-1", k8s: "1.29.4-eks" },
  "prod-eks-02": { provider: "aws", region: "us-west-2", k8s: "1.29.4-eks" },
  "stage-eks-01": { provider: "aws", region: "us-east-1", k8s: "1.28.8-eks" },
  "dev-eks-01": { provider: "aws", region: "eu-west-1", k8s: "1.28.8-eks" },
};

function healthScore(score: number): HealthScore {
  const grade = score >= 90 ? "A" : score >= 80 ? "B" : score >= 70 ? "C" : score >= 55 ? "D" : "F";
  const status = score >= 85 ? "healthy" : score >= 65 ? "warning" : "critical";
  return { score, grade, status, factors: [{ label: "Cluster rollup", impact: 0 }] };
}

export function clusterSummaryFor(
  clusterId: string,
  services: Service[],
  podsByService: Map<string, Pod[]>,
): ClusterSummary {
  const rng = makeRng(clusterId.length * 17);
  const meta = CLUSTER_META[clusterId] ?? { provider: "aws" as const, region: "us-east-1", k8s: "1.28.8-eks" };
  const inCluster = services.filter((s) => s.deployment.cluster === clusterId);
  let podCount = 0;
  let containerCount = 0;
  const nsSet = new Set<string>();
  for (const s of inCluster) {
    if (s.deployment.namespace) nsSet.add(s.deployment.namespace);
    const pods = podsByService.get(s.id) ?? [];
    podCount += pods.length;
    for (const p of pods) {
      containerCount += p.containers.length + p.sidecars.length + p.initContainers.length;
    }
  }
  const nodeCount = int(rng, 6, 24);
  const totalCpu = nodeCount * int(rng, 8, 16);
  const usedCpu = Math.round(totalCpu * (0.45 + rng() * 0.35));
  const totalMem = nodeCount * int(rng, 32, 64);
  const usedMem = Math.round(totalMem * (0.5 + rng() * 0.3));

  const namespaces = [...nsSet].map((name) => ({
    name,
    pods: int(rng, 2, 18),
    health: healthScore(int(rng, 72, 98)),
  }));

  return {
    id: clusterId,
    name: clusterId,
    cloudProvider: meta.provider,
    region: meta.region,
    kubernetesVersion: meta.k8s,
    nodeCount,
    namespaceCount: nsSet.size || int(rng, 4, 12),
    runningPods: podCount || int(rng, 40, 120),
    runningContainers: containerCount || int(rng, 80, 240),
    deployments: inCluster.length || int(rng, 12, 40),
    statefulSets: int(rng, 2, 8),
    daemonSets: int(rng, 3, 6),
    services: inCluster.length + int(rng, 5, 15),
    ingresses: int(rng, 4, 12),
    jobs: int(rng, 1, 6),
    cronJobs: int(rng, 2, 10),
    pvcs: int(rng, 8, 24),
    storageClasses: int(rng, 2, 5),
    totalCpuCores: totalCpu,
    usedCpuCores: usedCpu,
    totalMemoryGb: totalMem,
    usedMemoryGb: usedMem,
    availableCapacityPct: Math.round(((totalCpu - usedCpu) / totalCpu) * 100),
    costEstimateUsdMonth: int(rng, 4200, 18500),
    health: healthScore(int(rng, 78, 96)),
    namespaces,
    updatedAt: new Date().toISOString(),
  };
}

export function clusterTopologyFor(
  clusterId: string,
  services: Service[],
): ClusterTopology {
  const inCluster = services.filter((s) => s.deployment.cluster === clusterId).slice(0, 12);
  const nodes: TopologyNode[] = [];
  const edges: TopologyEdge[] = [];

  nodes.push({
    id: "ingress_main", kind: "ingress", name: "main-ingress", status: "healthy",
    path: `/clusters/${clusterId}`, x: 400, y: 40, group: "edge",
  });

  const nsGroups = new Map<string, Service[]>();
  for (const s of inCluster) {
    const ns = s.deployment.namespace ?? "default";
    if (!nsGroups.has(ns)) nsGroups.set(ns, []);
    nsGroups.get(ns)!.push(s);
  }

  let nsIdx = 0;
  for (const [ns, svcs] of nsGroups) {
    const baseY = 120 + nsIdx * 180;
    nodes.push({
      id: `ns_${ns}`, kind: "namespace", name: ns, status: "healthy",
      path: `/clusters/${clusterId}?namespace=${ns}`, x: 80, y: baseY, group: ns,
    });
    svcs.forEach((s, i) => {
      const x = 220 + (i % 4) * 140;
      const y = baseY + Math.floor(i / 4) * 70;
      nodes.push({
        id: s.id, kind: "microservice", name: s.name, status: s.status,
        path: `/services/${s.id}`, x, y, group: ns,
        metrics: { latencyMs: s.latencyP95Ms, errorPct: s.errorRatePct, rps: Math.round(s.requestsPerMin / 60) },
      });
      edges.push({
        id: `e_ingress_${s.id}`, from: "ingress_main", to: s.id,
        protocol: "HTTP", latencyMs: s.latencyP50Ms, errorPct: s.errorRatePct, rps: Math.round(s.requestsPerMin / 60),
      });
      if (i > 0) {
        const rng2 = makeRng(i);
        edges.push({
          id: `e_${svcs[0].id}_${s.id}`, from: svcs[0].id, to: s.id,
          latencyMs: int(rng2, 2, 40), errorPct: s.errorRatePct * 0.5, rps: 50,
        });
      }
    });
    nsIdx++;
  }

  return { clusterId, nodes, edges };
}

export function k8sObjectsFor(
  clusterId: string,
  services: Service[],
  podsByService?: Map<string, Pod[]>,
): K8sObjectSummary[] {
  const rng = makeRng(99);
  const inCluster = services.filter((s) => s.deployment.cluster === clusterId);
  const items: K8sObjectSummary[] = [];
  const nodeSet = new Set<string>();

  for (const s of inCluster) {
    items.push({
      kind: "deployment", name: s.name, namespace: s.deployment.namespace ?? "default",
      clusterId, status: s.status, age: `${int(rng, 1, 30)}d`, path: `/services/${s.id}`,
    });
    items.push({
      kind: "service", name: `${s.name}-svc`, namespace: s.deployment.namespace ?? "default",
      clusterId, status: "healthy", age: `${int(rng, 5, 60)}d`, path: `/services/${s.id}`,
    });
    items.push({
      kind: "ingress", name: `${s.name}-ingress`, namespace: s.deployment.namespace ?? "default",
      clusterId, status: "healthy", age: `${int(rng, 3, 45)}d`, path: `/services/${s.id}`,
    });

    const pods = podsByService?.get(s.id) ?? [];
    for (const p of pods) {
      nodeSet.add(p.node);
      items.push({
        kind: "pod", name: p.name, namespace: p.namespace,
        clusterId, status: p.status === "running" ? "healthy" : p.status === "pending" ? "warning" : "critical",
        age: `${int(rng, 1, 14)}d`, path: `/containers/${s.id}/${p.id}/${encodeURIComponent(p.containers[0]?.name ?? "app")}`,
      });
      for (const c of p.containers) {
        items.push({
          kind: "pod", name: `${p.name}/${c.name}`, namespace: p.namespace,
          clusterId, status: c.status === "running" ? "healthy" : "warning",
          age: `${int(rng, 1, 14)}d`, path: `/containers/${s.id}/${p.id}/${encodeURIComponent(c.name)}`,
        });
      }
    }
  }

  const nodeNames = nodeSet.size > 0 ? [...nodeSet] : Array.from({ length: int(rng, 4, 8) }, (_, i) => `ip-10-0-${i + 1}.ec2.internal`);
  for (const node of nodeNames) {
    items.push({
      kind: "node", name: node, namespace: "—",
      clusterId, status: rng() > 0.1 ? "healthy" : "warning",
      age: `${int(rng, 30, 400)}d`, path: `/clusters/${clusterId}/objects?kind=node`,
    });
  }

  const nsSet = new Set(inCluster.map((s) => s.deployment.namespace ?? "default"));
  for (const ns of nsSet) {
    items.push({
      kind: "namespace", name: ns, namespace: ns,
      clusterId, status: "healthy", age: `${int(rng, 60, 500)}d`,
      path: `/clusters/${clusterId}?namespace=${ns}`,
    });
  }

  return items;
}

export function deploymentTimelineFor(serviceId: string, serviceName: string): DeploymentTimelineEntry[] {
  const rng = makeRng(serviceId.length);
  return Array.from({ length: 8 }, (_, i) => {
    const success = i === 0 ? false : rng() > 0.12;
    const started = new Date(Date.now() - (i + 1) * 86400000 * 2).toISOString();
    const dur = int(rng, 45, 420);
    const strategy = pickStrategy(rng, i);
    const podsReplaced = int(rng, 2, 12);
    const imageTag = `v1.${4 - Math.floor(i / 2)}.${i}`;
    const revision = `rev-${120 - i}`;
    const completedAt = new Date(new Date(started).getTime() + dur * 1000).toISOString();
    const gitCommit = `a${int(rng, 100000, 999999).toString(16)}f`;

    const base: DeploymentTimelineEntry = {
      id: `tl_${serviceId}_${i}`,
      serviceId,
      serviceName,
      revision,
      imageTag,
      startedAt: started,
      completedAt,
      durationSec: dur,
      success,
      strategy,
      healthChecksPassed: success ? int(rng, 3, 5) : int(rng, 0, 2),
      podsReplaced,
      trafficShiftPct: i === 0 ? 100 : strategy === "canary" ? int(rng, 20, 100) : undefined,
      gitCommit,
      gitBranch: i === 0 ? "main" : "release",
      image: `registry.coremonit.io/${serviceName}:${imageTag}`,
      namespace: "production",
      cluster: "prod-eks-01",
      triggeredBy: i === 0 ? "ci-pipeline" : "manual",
      syncStatus: success ? "synced" : "outofsync",
      healthStatus: success ? "healthy" : "critical",
      events: buildDeploymentEvents(started, completedAt, success, serviceName, strategy, podsReplaced),
      resourceTree: buildResourceTree(serviceName, revision, podsReplaced, success),
    };

    if (!success) {
      base.failureReason = strategy === "canary"
        ? "Canary analysis failed: error rate exceeded 5% threshold during traffic shift"
        : "Rollout failed: readiness probe did not pass within timeout";
      base.errorDetails = [
        `Readiness probe failed: Get "http://10.0.${int(rng, 1, 9)}.${int(rng, 10, 99)}:8080/health": dial tcp: connection refused`,
        `Pod ${serviceName}-${revision}-x7k2m: CrashLoopBackOff (back-off 5m0s restarting failed container)`,
        `Liveness probe failed: HTTP probe failed with statuscode: 503`,
        `HorizontalPodAutoscaler: unable to scale — current replicas above max`,
      ];
      base.podLogs = [{
        pod: `${serviceName}-${revision}-x7k2m`,
        container: serviceName,
        lines: [
          `[ERROR] Unhandled exception during startup`,
          `Error: connect ECONNREFUSED 127.0.0.1:5432`,
          `    at TCPConnectWrap.afterConnect [as oncomplete] (node:net:1611:16)`,
          `    at Object.<anonymous> (/app/dist/server.js:142:11)`,
          `[WARN]  Health check /health returned 503 — database pool not ready`,
          `[FATAL] Process exited with code 1`,
        ],
      }];
    }

    return base;
  });
}

function buildDeploymentEvents(
  started: string,
  completed: string,
  success: boolean,
  serviceName: string,
  strategy: DeploymentTimelineEntry["strategy"],
  pods: number,
): DeploymentTimelineEntry["events"] {
  const t0 = new Date(started).getTime();
  const mk = (offsetSec: number, kind: DeploymentTimelineEntry["events"][number]["kind"], message: string, resource?: string, detail?: string) => ({
    ts: new Date(t0 + offsetSec * 1000).toISOString(),
    kind, message, resource, detail,
  });

  const events: DeploymentTimelineEntry["events"] = [
    mk(0, "created", "Deployment initiated", serviceName, `Strategy: ${strategy.replace("_", " ")}`),
    mk(5, "info", "Pulling container image", `${serviceName}:${strategy}`, "Image pull started"),
    mk(18, "progressed", `Scaled up ReplicaSet ${serviceName}-rs`, `${serviceName}-rs`, `replicas: ${pods}`),
    mk(32, "info", "Created pod", `${serviceName}-new-1`, "Scheduled on ip-10-0-4.ec2.internal"),
    mk(45, "progressed", "Started container", `${serviceName}-new-1`, "Container main started"),
  ];

  if (strategy === "canary") {
    events.push(mk(55, "info", "Canary traffic shift 25%", serviceName, "Routing 25% via istio/virtual-service"));
    events.push(mk(68, "info", "Canary traffic shift 50%", serviceName, "Routing 50% via istio/virtual-service"));
  }

  if (success) {
    events.push(mk(80, "progressed", "Readiness probe succeeded", `${serviceName}-new-1`, "HTTP 200 on /health"));
    events.push(mk(95, "synced", "Rollout complete", serviceName, `All ${pods} pods healthy`));
  } else {
    events.push(mk(62, "warning", "Readiness probe failed", `${serviceName}-new-1`, "HTTP probe failed: status 503"));
    events.push(mk(70, "failed", "Container crashed", `${serviceName}-new-1`, "Exit code 1 — CrashLoopBackOff"));
    events.push(mk(75, "degraded", "Rollout paused — auto rollback disabled", serviceName, "Manual intervention required"));
    events.push(mk(90, "failed", "Rollout failed health checks", serviceName, "Exceeded maxUnavailable threshold"));
    events.push(mk(100, "degraded", "Deployment marked OutOfSync", serviceName, completed));
  }

  return events;
}

function buildResourceTree(
  serviceName: string,
  revision: string,
  podCount: number,
  success: boolean,
): DeploymentTimelineEntry["resourceTree"] {
  const rsName = `${serviceName}-${revision}`;
  const pods: DeploymentTimelineEntry["resourceTree"] = Array.from({ length: Math.min(podCount, 4) }, (_, i) => {
    const failed = !success && i === 0;
    return {
      id: `pod_${i}`,
      kind: "pod" as const,
      name: `${rsName}-${["x7k2m", "p9f3n", "k2j8q", "m4h1r"][i] ?? i}`,
      status: failed ? "critical" : "healthy",
      health: failed ? "critical" : "healthy",
      message: failed ? "CrashLoopBackOff · 3 restarts" : "Running",
    };
  });

  return [{
    id: "dep_root",
    kind: "deployment",
    name: serviceName,
    status: success ? "healthy" : "critical",
    health: success ? "healthy" : "critical",
    message: success ? "Synced" : "OutOfSync · Degraded",
    children: [{
      id: "rs_root",
      kind: "replicaset",
      name: rsName,
      status: success ? "healthy" : "warning",
      health: success ? "healthy" : "critical",
      message: `${podCount} desired · ${success ? podCount : podCount - 1} ready`,
      children: pods,
    }, {
      id: "svc_root",
      kind: "service",
      name: `${serviceName}-svc`,
      status: "healthy",
      health: "healthy",
    }],
  }];
}

function pickStrategy(rng: () => number, i: number): DeploymentTimelineEntry["strategy"] {
  if (i === 0) return "canary";
  if (rng() < 0.3) return "blue_green";
  return "rolling";
}

export function containerDetailFor(
  service: Service,
  pod: Pod,
  containerName: string,
): ContainerDetail | null {
  const all = [...pod.containers, ...pod.sidecars, ...pod.initContainers];
  const c = all.find((x) => x.name === containerName);
  if (!c) return null;
  const tag = pod.image.split(":")[1] ?? "latest";
  return {
    ...c,
    podId: pod.id,
    podName: pod.name,
    serviceId: service.id,
    serviceName: service.name,
    cluster: service.deployment.cluster ?? "unknown",
    namespace: pod.namespace,
    node: pod.node,
    imageDigest: pod.imageDigest,
    imageTag: tag,
    limits: { cpu: "2000m", memory: "4Gi" },
    requests: { cpu: "500m", memory: "1Gi" },
    oomEvents24h: service.oomKills24h,
    startupTimeSec: int(makeRng(pod.id.length), 2, 45),
    uptimeSec: pod.uptimeSec,
    openPorts: [8080, 9090],
    env: pod.env,
    volumes: pod.volumes,
    secrets: pod.secrets,
    health: {
      score: c.status === "running" ? 94 : 42,
      grade: c.status === "running" ? "A" : "D",
      status: c.status === "running" ? "healthy" : "critical",
      factors: [{ label: "Restarts", impact: c.restartCount > 2 ? -15 : 0, detail: `${c.restartCount} restarts` }],
    },
    aiSummary: c.status === "running"
      ? `${c.name} is healthy on ${pod.node}. CPU at ${c.cpuMillicoresLive}m, memory ${c.memoryMbLive}MB. No anomalies in the last hour.`
      : `${c.name} is not running — investigate events and recent deployment on ${service.name}.`,
    links: {
      serviceId: service.id,
      podId: pod.id,
      containerName: c.name,
      clusterId: service.deployment.cluster,
      namespace: pod.namespace,
      deploymentId: undefined,
      imageTag: tag,
    },
  };
}
